import { translations } from '@internal/i18n/translations'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { TLocale } from '@internal/utils/machine/i18n/TLocale'
import { MachineType } from '@internal/utils/machine/MachineType'
import { useMachineTypeName } from '@internal/utils/machine/useMachineTypeName'
import { Search } from '@mui/icons-material'
import {
  AutocompleteRenderInputParams,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { useMemo } from 'react'
import styles from './searchInput.module.scss'
import { useTypewriterEffect } from './useTypewriterEffect'

type SearchInputProps = {
  locale: TLocale
  endAdornmentText?: string
  isLoading: boolean
  showPlaceholder: boolean
  onSearchButtonClick: () => void
} & AutocompleteRenderInputParams

export const SearchInput: React.FC<SearchInputProps> = (props) => {
  const {
    locale,
    endAdornmentText,
    isLoading,
    showPlaceholder,
    onSearchButtonClick,
    ...inputPropsRest
  } = props
  const { t } = useTranslation(localeNamespace.common)
  const { getMachineTypeName, lowerCaseMachineTypeName } = useMachineTypeName()

  const placeholderTranslation = t(translations.searchInputPlaceholder)
  const words = useMemo(
    () => [
      lowerCaseMachineTypeName(
        getMachineTypeName(MachineType.TRACTOR, 'plural'),
        locale
      ),
      lowerCaseMachineTypeName(
        getMachineTypeName(MachineType.COMBINE_HARVESTER, 'plural'),
        locale
      ),
      lowerCaseMachineTypeName(
        getMachineTypeName(MachineType.BALER, 'plural'),
        locale
      ),
      t(translations.searchInputBrandsValue),
      'Fendt 724',
      'John Deere 8R',
    ],
    [getMachineTypeName, locale, lowerCaseMachineTypeName, t]
  )
  const placeholderText = useTypewriterEffect({
    enabled: showPlaceholder,
    placeHolderText: placeholderTranslation,
    words,
  })

  return (
    <TextField
      {...inputPropsRest}
      className={styles.searchInputWrapper}
      inputProps={{
        ...inputPropsRest.inputProps,
        placeholder: showPlaceholder ? placeholderText : undefined,
        className: classNames(
          styles.searchInput,
          inputPropsRest.inputProps?.className
        ),
      }}
      variant='outlined'
      InputProps={{
        ...inputPropsRest.InputProps,
        endAdornment: (
          <Stack direction='row' alignItems='center' gap={1}>
            {props.endAdornmentText && (
              <Typography
                variant='caption'
                color='text.secondary'
                style={{ lineHeight: 1 }}
              >
                {props.endAdornmentText}
              </Typography>
            )}

            <CircularProgress
              color='inherit'
              size={20}
              style={{
                visibility: isLoading ? 'visible' : 'hidden',
              }}
            />

            {inputPropsRest.InputProps.endAdornment}

            <IconButton
              className={styles.iconButton}
              onClick={onSearchButtonClick}
            >
              <Search fontSize='small' />
            </IconButton>
          </Stack>
        ),
      }}
    />
  )
}

SearchInput.displayName = 'SearchInput'
